import { Capacitor } from '@capacitor/core';
import { database } from './firebase/core';
import logger from '../utils/logger';

/**
 * Simplified iOS lifecycle service - minimal approach for production
 * Relies on iOS native auth persistence and Firebase's built-in reconnection
 */
export const setupIOSLifecycleHandlers = () => {
  if (Capacitor.getPlatform() !== 'ios' && Capacitor.getPlatform() !== 'android') {
    return; // No setup needed for web platform
  }

  logger.info('📱 Native platform lifecycle handlers: minimal setup for production');

  const { App } = require('@capacitor/app');

  // CRITICAL: Check if app was launched with a URL (cold start deep link)
  // This needs to happen BEFORE we set up the listener, because the event
  // may have already fired during app launch
  App.getLaunchUrl().then((result: { url: string } | null) => {
    if (result && result.url) {
      logger.info('📲 App launched with URL:', result.url);

      // Process the launch URL the same way we process appUrlOpen events
      try {
        const url = new URL(result.url);
        const path = url.pathname;

        logger.info('📲 Launch URL path:', path);

        // Build deep link path from URL (supports any path, not just gymtimer)
        let deepLinkPath = path;

        // Include query string if present
        if (url.search) {
          deepLinkPath += url.search;
        }

        // Ensure path starts with /
        if (!deepLinkPath.startsWith('/')) {
          deepLinkPath = '/' + deepLinkPath;
        }

        logger.info('📲 Launch URL deep link path:', deepLinkPath);

        if (typeof window !== 'undefined') {
          (window as any).__pendingDeepLink = deepLinkPath;
          logger.info('📲 Stored pending deep link from launch URL:', deepLinkPath);

          // Dispatch event
          const deepLinkEvent = new CustomEvent('deeplink', { detail: { path: deepLinkPath } });
          window.dispatchEvent(deepLinkEvent);
          logger.info('📲 Dispatched deeplink event from launch URL');
        }
      } catch (error) {
        logger.error('❌ Error processing launch URL:', error);
      }
    } else {
      logger.info('📲 App launched normally (no URL)');
    }
  }).catch((error: any) => {
    logger.error('❌ Error getting launch URL:', error);
  });

  // Handle app state changes (foreground/background)
  App.addListener('appStateChange', async (state: { isActive: boolean }) => {
    if (state.isActive) {
      // Simple database reconnection - Firebase handles the rest
      try {
        const { goOnline } = await import('firebase/database');
        goOnline(database);
      } catch (error) {
        // Silent failure - database will reconnect automatically
      }

      // Trigger background health sync (non-blocking)
      // Uses dynamic import to avoid circular dependencies
      try {
        const { healthSyncService } = await import('./health/health-sync.service');
        healthSyncService.performBackgroundSync().catch(() => {});
      } catch (error) {
        // Silent failure - health sync is optional
      }
    }
  });

  // Handle deep links (QR code scanning for gym timer)
  // This catches deep links when app is already running (warm start)
  logger.info('📲 Registering appUrlOpen listener for deep links...');
  App.addListener('appUrlOpen', (event: { url: string }) => {
    logger.info('📲 Deep link opened (appUrlOpen event):', event.url);

    try {
      // Extract path from URL
      // Expected format: https://marune.varsalus.com/gymtimer/join/742851
      //              or: maruneapp://gymtimer/join/742851
      const url = new URL(event.url);
      const path = url.pathname;

      logger.info('📲 Deep link path extracted:', path);
      logger.info('📲 Full URL object:', { pathname: url.pathname, href: url.href, host: url.host });

      // Build deep link path from URL (supports any path)
      let deepLinkPath = path;

      // Include query string if present
      if (url.search) {
        deepLinkPath += url.search;
      }

      // Ensure path starts with /
      if (!deepLinkPath.startsWith('/')) {
        deepLinkPath = '/' + deepLinkPath;
      }

      logger.info('📲 Processing deep link path:', deepLinkPath);

      // Store the deep link for DeepLinkHandler to process
      if (typeof window !== 'undefined') {
        (window as any).__pendingDeepLink = deepLinkPath;
        logger.info('📲 Stored pending deep link on window:', deepLinkPath);
        logger.info('📲 Window object has __pendingDeepLink:', !!(window as any).__pendingDeepLink);

        // Also dispatch a custom event as a backup
        const deepLinkEvent = new CustomEvent('deeplink', { detail: { path: deepLinkPath } });
        window.dispatchEvent(deepLinkEvent);
        logger.info('📲 Dispatched deeplink custom event');
      }
    } catch (error) {
      logger.error('❌ Error handling deep link:', error);
    }
  });
};

const iosLifecycleService = {
  setupIOSLifecycleHandlers
};

export default iosLifecycleService;